import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { DataContext } from "../shared/DataContext";
import { Rating } from '../components/Rating';
import { MarketplaceIcon } from '../components/MarketplaceIcon';
import { InsightCard } from '../components/InsightCard';
import { ServiceCompanyStatus } from '../components/ServiceCompanyStatus';
import { CTAFooter } from "../components/common/CTAFooter";
import back_button from '../../assets/back_button.png';
import share_button from '../../assets/share_button.png';
import heart_empty from '../../assets/heart_empty.png';
import heart_filled from '../../assets/heart_filled.png';
import address_icon from '../../assets/address_icon.png';
import phone_icon from '../../assets/phone_icon.png';
import email_icon from '../../assets/email_icon.png';
import homepage_icon from '../../assets/homepage_icon.png';
import location_icon from '../../assets/location_icon.png';
import marketplace_icon from '../../assets/marketplace_icon.png';
import yelp_enabled from '../../assets/yelp_enabled.png';
import yelp_disabled from '../../assets/yelp_disabled.png';
import home_advisor_enabled from '../../assets/home_advisor_enabled.png';
import home_advisor_disabled from '../../assets/home_advisor_disabled.png';
import thumbtack_enabled from '../../assets/thumbtack_enabled.png';
import thumbtack_disabled from '../../assets/thumbtack_disabled.png';
import task_rabbit_enabled from '../../assets/task_rabbit_enabled.png';
import task_rabbit_disabled from '../../assets/task_rabbit_disabled.png';
import other_enabled from '../../assets/other_enabled.png';
import other_disabled from '../../assets/other_disabled.png';
import '../../css/ServiceCompanyProfile.css';
import SharePopup from "../components/SharePopup";

function ServiceCompanyProfile() {
    const navigate = useNavigate();
    const { currentUser } = useContext(DataContext);
    const { id } = useParams();
    const [serviceCompany, setServiceCompany] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saved, setSaved] = useState(false);
    const [isOwner, setIsOwner] = useState(false);
    const [showSharePopup, setShowSharePopup] = useState(false);
    const [showOwnButton, setShowOwnButton] = useState(false);

    useEffect(() => {
        const fetchServiceCompany = async () => {
            try {
                const response = await axios.get(`/api/service_providers/${id}`);
                setServiceCompany(response.data);
                setLoading(false);
            } catch (err) {
                setError('Failed to fetch service company data');
                setLoading(false);
            }
        };

        fetchServiceCompany();
    }, [id]);

    useEffect(() => {
        if (serviceCompany && currentUser?.user_type === 'ServiceCompany' && currentUser?.entity_id === serviceCompany.id) {
            setIsOwner(true);
        } else if (serviceCompany?.status === "Internal Complete") {
            setShowOwnButton(true);
        }
    }, [serviceCompany, currentUser]);

    useEffect(() => {
        if (id && currentUser?.user_type === 'Agent') {
            const fetchSaved = async () => {
                try {
                    const response = await axios.get(`/api/get_saved?id=${id}`);
                    setSaved(response.data.saved);
                } catch (err) {
                    setError('Failed to fetch service company saved data');
                    console.error(err);
                }
            };

            fetchSaved();
        }
    }, [id, currentUser]);

    const handleShareClick = async () => {
        setShowSharePopup(true);
    };

    const toggleSave = async () => {
        try {
            const response = await axios.post(`/api/save_service_provider`, {
                service_provider_id: serviceCompany.id
            });

            setSaved(response.data.saved);
        } catch (err) {
            console.error("Failed to toggle save:", err);
        }
    }

    const handleOwnClick = async () => {
        navigate(`/service-company-registration?id=${serviceCompany.link_hash}`);
    }

    if (loading) {
        return <div className="service-company-profile-container">Loading...</div>;
    }

    if (error) {
        return <div className="service-company-profile-container">Error: {error}</div>;
    }

    return (
        <div className="service-company-profile-container">
            <div className="service-company-profile-company-info">
                <div className="service-company-profile-image-preview-small">
                    <img src={serviceCompany.image} alt={serviceCompany.name}/>
                </div>
                <div className="service-company-profile-title-section">
                    <div className="service-company-profile-company-name">
                        <a href="/directory">
                            <img
                                loading="lazy"
                                src={back_button}
                                alt="Company icon"
                                className="service-company-profile-back-arrow"
                            />
                        </a>
                        <span className="service-company-profile-name">{serviceCompany.name}</span>
                    </div>
                    <ServiceCompanyStatus status={serviceCompany.status} showOwnButton={showOwnButton} handleOwnClick={handleOwnClick}/>
                </div>

                <div className="service-company-profile-details">
                    <div className="service-company-profile-rating-group">
                        <Rating
                            score={serviceCompany.yelp_rating}
                            reviews={serviceCompany.yelp_count}
                            url={serviceCompany.yelp_url}
                            platform="Yelp"
                        />
                        <Rating
                            score={serviceCompany.google_rating}
                            reviews={serviceCompany.google_count}
                            url={serviceCompany.google_url}
                            platform="Google"
                        />
                    </div>
                    <div className="service-company-profile-share-actions">
                        <div className="service-company-profile-action-button-container">
                            <div className="service-company-profile-action-button" onClick={handleShareClick}>
                                <img
                                    loading="lazy"
                                    src={share_button}
                                    alt="Share icon"
                                    className="service-company-profile-action-icon"
                                />
                                Share
                            </div>
                        </div>
                        {currentUser?.user_type === 'Agent' && (
                            <div className="service-company-profile-action-button-container">
                                <div className="service-company-profile-action-button" onClick={toggleSave}>
                                    <img
                                        loading="lazy"
                                        src={saved ? heart_filled : heart_empty}
                                        alt="Save icon"
                                        className="service-company-profile-action-icon"
                                    />
                                    Save
                                </div>
                            </div>
                        )}
                        {isOwner && false && (
                            <div className="service-company-profile-action-button-container" onClick={toggleSave}>
                                <div className="service-company-profile-edit-button">
                                    Edit
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="service-company-profile-content-grid">
                    <aside className="service-company-profile-insight-section">
                        <div className="service-company-profile-image-preview">
                            <img src={serviceCompany.image} alt={serviceCompany.name}/>
                        </div>
                        <div className="service-company-profile-relationship-insight-large">
                        <div className="service-company-profile-insight-title">{serviceCompany.name} Relationship
                                Insight
                            </div>
                            <div className="service-company-profile-insight-grid">
                                <InsightCard key={1} label="Connected Agents" count={serviceCompany.connected_agents}
                                             hoverText="Connected Agents (CA) refers to the number of Realtor clients."/>
                                <InsightCard key={2} label="Service Records" count={serviceCompany.service_records}
                                             hoverText="Service Records (SR) refers to the number of Realtor hired jobs."/>
                                <InsightCard key={3} label="Recurring Agents Records"
                                             count={serviceCompany.recurring_agents_records}
                                             hoverText="Recurring Agents Records (RAR) refers to the number of Realtors repeatedly hired."/>
                            </div>
                        </div>
                    </aside>

                    <section className="service-company-profile-contact-card">
                        <div className="service-company-profile-contact-name"><div>{serviceCompany.contact_name}</div>
                            {serviceCompany.license_number && (
                                <div className="service-company-profile-license-number">
                                    lic # {serviceCompany.license_number}
                                </div>
                            )}</div>
                        <div className="service-company-profile-contact-info">
                            {serviceCompany.address_1 && (
                                <div className="service-company-profile-info-item">
                                    <img
                                        loading="lazy"
                                        src={address_icon}
                                        alt="Address icon"
                                        className="service-company-profile-info-icon"
                                    />
                                    <div className="service-company-profile-address">
                                        {serviceCompany.address_1}{serviceCompany.address_2 ? ", " + serviceCompany.address_2 : ""}<br/>
                                        {serviceCompany.city}, {serviceCompany.state} {serviceCompany.zip}
                                    </div>
                                </div>
                            )}
                            {serviceCompany.phone && (
                                <div className="service-company-profile-info-item">
                                    <img
                                        loading="lazy"
                                        src={phone_icon}
                                        alt="Phone icon"
                                        className="service-company-profile-info-icon"
                                    />
                                    {serviceCompany.phone && (
                                        <a href="tel:415-555-5555"
                                           className="service-company-profile-phone">{serviceCompany.phone}</a>
                                    )}
                                </div>
                            )}
                            {serviceCompany.email && (
                                <div className="service-company-profile-info-item">
                                    <img
                                        loading="lazy"
                                        src={email_icon}
                                        alt="Email icon"
                                        className="service-company-profile-info-icon"
                                    />
                                    <a href="mailto:info@serviceco.com"
                                       className="service-company-profile-email">{serviceCompany.email}</a>
                                </div>
                            )}
                            {serviceCompany.homepage_url && (
                                <div className="service-company-profile-info-item">
                                    <img
                                        loading="lazy"
                                        src={homepage_icon}
                                        alt="Homepage icon"
                                        className="service-company-profile-info-icon"
                                    />
                                    <a href={"https://" + serviceCompany.homepage_url} target="_blank"
                                       className="service-company-profile-url">
                                        {serviceCompany.homepage_url}
                                    </a>
                                </div>
                            )}
                        </div>

                        <div className="service-company-profile-divider"/>

                        <div className="service-company-profile-service-area">
                            <div className="service-company-profile-info-item">
                                <img
                                    loading="lazy"
                                    src={location_icon}
                                    alt="Location icon"
                                    className="service-company-profile-info-icon"
                                />
                                <span>Service Cities: {serviceCompany.cities.map(city => city.name).join(', ')}</span>
                            </div>
                            <div className="service-company-profile-info-item">
                                <img
                                    loading="lazy"
                                    src={marketplace_icon}
                                    alt="Marketplace icon"
                                    className="service-company-profile-info-icon"
                                />
                                <span>Marketplace URL:</span>
                            </div>
                        </div>

                        <div className="service-company-profile-marketplaces">
                            <MarketplaceIcon key={1} name="Yelp" shortName="Yelp" url={serviceCompany.yelp_url}
                                             enabledIconSrc={yelp_enabled}
                                             disabledIconSrc={yelp_disabled}/>
                            <MarketplaceIcon key={2} name="HomeAdvisor" shortName="Home Advisor"
                                             url={serviceCompany.home_advisor_url}
                                             enabledIconSrc={home_advisor_enabled}
                                             disabledIconSrc={home_advisor_disabled}/>
                            <MarketplaceIcon key={3} name="Thumbtack" shortName="Thumb- tack"
                                             url={serviceCompany.thumbtack_url}
                                             enabledIconSrc={thumbtack_enabled} disabledIconSrc={thumbtack_disabled}/>
                            <MarketplaceIcon key={4} name="TaskRabbit" shortName="Task Rabbit"
                                             url={serviceCompany.task_rabbit_url}
                                             enabledIconSrc={task_rabbit_enabled}
                                             disabledIconSrc={task_rabbit_disabled}/>
                            <MarketplaceIcon key={5} name="Other" shortName="Other" url={serviceCompany.other_url}
                                             enabledIconSrc={other_enabled} disabledIconSrc={other_disabled}/>
                        </div>

                        {showOwnButton && (
                            <div className="service-company-profile-own-button-container">
                                <div className="service-company-profile-divider"/>

                                <button className="service-company-profile-claim-button" onClick={handleOwnClick}>
                                    I own {serviceCompany.name}
                                </button>
                            </div>
                        )}
                    </section>
                </div>
                <div className="service-company-profile-relationship-insight-medium">
                    <div className="service-company-profile-insight-title">{serviceCompany.name} Relationship
                        Insight
                    </div>
                    <div className="service-company-profile-insight-grid">
                        <InsightCard key={1} label="Connected Agents" count={serviceCompany.connected_agents}
                                     hoverText="Connected Agents (CA) refers to the number of Realtor clients."/>
                        <InsightCard key={2} label="Service Records" count={serviceCompany.service_records}
                                     hoverText="Service Records (SR) refers to the number of Realtor hired jobs."/>
                        <InsightCard key={3} label="Recurring Agents Records"
                                     count={serviceCompany.recurring_agents_records}
                                     hoverText="Recurring Agents Records (RAR) refers to the number of Realtors repeatedly hired."/>
                    </div>
                </div>
                {showSharePopup && <SharePopup setShowSharePopup={setShowSharePopup} company={serviceCompany} />}
                <CTAFooter/>
            </div>
        </div>
    );
}

export default ServiceCompanyProfile;
